import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import BkashExecutePaymentAPICall from '../../action/BkashExecutePaymentAPICall';
import axios from 'axios';
import facebookPixel from '../../utils/facebookPixel';
import { URL } from '../AxiosUrlGlobal';

// Platform rate for the Facebook Purchase value: $1 = ৳125
// (৳500 = $4, ৳5,000 = $40, the ৳1,000 launch offer = $8).
const BDT_PER_USD = 125;

function Callback() {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const paymentID = searchParams.get('paymentID');
    const bkashStatus = searchParams.get('status');

    const [view, setView] = useState('processing'); // 'processing' | 'success'

    useEffect(() => {
        // Where this payment was started (/get-started or /billing) — on failure
        // we send the user back there with an error; default to /billing.
        const payReturn = localStorage.getItem('payReturn') || '/billing';
        const goError = (msg) => {
            // Deliver the error via sessionStorage so it survives the bKash
            // round-trip AND any client-side route bounce (the destination page
            // reads + clears it on mount). More reliable than a URL param.
            try {
                sessionStorage.setItem('payError', msg);
            } catch (e) {
                /* private mode / blocked storage — fall back to URL param below */
            }
            localStorage.removeItem('payReturn');
            const sep = payReturn.includes('?') ? '&' : '?';
            window.location.href = `${payReturn}${sep}payError=${encodeURIComponent(msg)}`;
        };

        async function run() {
            try {
                const response = await BkashExecutePaymentAPICall(paymentID);

                if (response?.statusCode === '0000') {
                    const actualAmount = response?.amount || 0;
                    const trxID = response?.trxID || paymentID;

                    // Activate the subscription. The endpoint returns hasStore so
                    // we route from the backend truth (no extra call).
                    try {
                        const act = await axios.post(`${URL}update-payment-ec/${paymentID}`, {
                            TransactionID: trxID,
                        });
                        if (act.status === 200) {
                            // Facebook Purchase — only after the subscription is
                            // actually activated (USD; BDT at $1 = ৳125). Never let
                            // a pixel error block the redirect.
                            try {
                                const fbPixelId = process.env.REACT_APP_FB_PIXEL_ID || '1398711768516449';
                                facebookPixel.init(fbPixelId, { autoConfig: true, debug: false });
                                facebookPixel.trackPurchase({
                                    currency: 'USD',
                                    value: (Number(actualAmount) / BDT_PER_USD).toFixed(2),
                                    transaction_id: trxID,
                                    content_type: 'product',
                                    content_name: 'Subscription Package',
                                });
                            } catch (e) {
                                /* ignore pixel errors */
                            }

                            const uid = localStorage.getItem('id');
                            const hasStore = act.data?.hasStore === true;
                            localStorage.setItem('IsPaid', 'true');
                            localStorage.setItem('offerActive', 'false');
                            localStorage.setItem('CreateStore', hasStore ? 'true' : 'false');
                            localStorage.removeItem('payReturn');
                            setView('success');
                            // Show success, then: no store → create store, else → dashboard.
                            setTimeout(() => {
                                window.location.href = hasStore ? '/dashboard' : `/set-store-name/${uid}`;
                            }, 1800);
                        } else {
                            axios
                                .post(`${URL}log-payment-error`, {
                                    paymentID,
                                    trxID,
                                    error: 'Activation returned non-200',
                                    response: act.data,
                                    timestamp: new Date().toISOString(),
                                })
                                .catch(() => {});
                            goError(`পেমেন্ট হয়েছে কিন্তু সক্রিয়করণে সমস্যা। সাপোর্টে ট্রানজ্যাকশন ID দিন: ${trxID}`);
                        }
                    } catch (actErr) {
                        axios
                            .post(`${URL}log-payment-error`, {
                                paymentID,
                                trxID,
                                amount: actualAmount,
                                error: 'Activation failed after payment',
                                errorMessage: actErr.message,
                                timestamp: new Date().toISOString(),
                            })
                            .catch(() => {});
                        goError(`পেমেন্ট হয়েছে কিন্তু সক্রিয়করণে সমস্যা। সাপোর্টে ট্রানজ্যাকশন ID দিন: ${trxID}`);
                    }
                } else {
                    // Payment execution failed.
                    axios
                        .post(`${URL}fail-payment-ec/${paymentID}`, { TransactionID: response?.trxID })
                        .catch(() => {});
                    goError(response?.statusMessage || 'পেমেন্ট ব্যর্থ হয়েছে। আবার চেষ্টা করুন।');
                }
            } catch (error) {
                goError('পেমেন্ট প্রসেসিং-এ সমস্যা হয়েছে। আবার চেষ্টা করুন।');
            }
        }

        if (bkashStatus === 'success' && paymentID) {
            run();
        } else {
            goError('পেমেন্ট সম্পন্ন হয়নি।');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bkashStatus, paymentID]);

    const wrap = {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f6f6f7',
        padding: 16,
        textAlign: 'center',
    };

    if (view === 'success') {
        return (
            <div style={wrap} lang="bn">
                <div>
                    <div
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            background: '#008060',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px',
                        }}
                    >
                        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <h2 style={{ margin: '0 0 6px', color: '#0f172a', fontWeight: 800 }}>পেমেন্ট সফল হয়েছে!</h2>
                    <p style={{ margin: 0, color: '#6d7175' }}>আপনাকে নিয়ে যাওয়া হচ্ছে…</p>
                </div>
            </div>
        );
    }

    return (
        <div style={wrap} lang="bn">
            <div>
                <div
                    style={{
                        width: 40,
                        height: 40,
                        border: '3px solid #d7e9e4',
                        borderTopColor: '#008060',
                        borderRadius: '50%',
                        margin: '0 auto 14px',
                        animation: 'cb-spin 0.8s linear infinite',
                    }}
                />
                <p style={{ margin: 0, color: '#6d7175' }}>পেমেন্ট প্রসেস হচ্ছে…</p>
                <style>{`@keyframes cb-spin{to{transform:rotate(360deg)}}`}</style>
            </div>
        </div>
    );
}

export default Callback;
